import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useUser } from '@/lib/auth';
import { useBuildInfoContext } from '@/views/build/context';
import { type CategoryInfoInputType } from '@/views/build/features/category-info';
import { useCategoryLabels } from '@/views/build/features/category-reader';
import { useBuildFormContext } from '@/views/build/form/schema';
import { getGradeNumber, isAllGrades } from '@/views/build/util';

import { type AiBuilderBuilderContext } from '../utils/types';

export const useAiBuilderBuilderContext = (): AiBuilderBuilderContext => {
  const { user } = useUser();
  const { t } = useTranslation(['common']);
  const { control } = useBuildFormContext();
  const { publishers, schoolInfoTree, subjectTree, userCountryCode } =
    useBuildInfoContext();
  const [
    schoolTypeId,
    gradeId,
    semesterId,
    subject,
    subSubject,
    publisher,
    customPublisher,
    isLastSchoolInfoSaved,
  ] = useWatch({
    control,
    name: [
      'schoolTypeId',
      'gradeId',
      'semesterId',
      'subject',
      'subSubject',
      'publisher',
      'customPublisher',
      'isLastSchoolInfoSaved',
    ],
  });
  const categoryValues = useMemo<CategoryInfoInputType>(
    () => ({
      schoolTypeId: schoolTypeId ?? '',
      gradeId,
      semesterId,
      subject: subject ?? '',
      subSubject,
      publisher,
      customPublisher,
      isLastSchoolInfoSaved: isLastSchoolInfoSaved ?? false,
    }),
    [
      customPublisher,
      gradeId,
      isLastSchoolInfoSaved,
      publisher,
      schoolTypeId,
      semesterId,
      subject,
      subSubject,
    ],
  );
  const labels = useCategoryLabels({
    categoryValues,
    schoolInfoTree,
    subjectTree,
    publishers: publishers?.[userCountryCode],
    userCountryCode,
  });
  const userName = user?.username?.trim() ?? '';
  const schoolLevel = labels.schoolTypeLabel?.trim() ?? '';
  const gradeNumber = getGradeNumber(gradeId);
  const grade = gradeNumber
    ? isAllGrades(gradeNumber)
      ? String(t('common:school.info.allGrades'))
      : String(t('common:school.info.nGrade', { n: gradeNumber }))
    : '';
  const subjectLabel = labels.subjectLabel?.trim() ?? '';

  return useMemo(
    () => ({
      userName,
      schoolLevel,
      grade,
      subject: subjectLabel,
    }),
    [grade, schoolLevel, subjectLabel, userName],
  );
};
